import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';

const Profile: React.FC = () => {
  // Ensure page loads at top
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);
  const { user, userProfile, userStats, updateUserProfile, signOut, darkMode, setDarkMode, refreshUserData } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: userProfile?.name || '',
    exam_date: userProfile?.exam_date || ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Reusable function to sync with Stripe
  const syncWithStripe = useCallback(async () => {
    if (!user?.id) {
      throw new Error('User not found. Please sign out and sign back in.');
    }
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002';
    console.log('🔄 Syncing with backend:', backendUrl);
    
    try {
      // Call backend to sync with Stripe
      const response = await fetch(`${backendUrl}/sync-subscription-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: userProfile?.email,
          userId: user?.id
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Sync response:', data);
        
        // Update the database with the synced status
        if (data.success && data.recommendedDatabaseUpdate) {
          const update = data.recommendedDatabaseUpdate;
          
          // Validation: Ensure we have all required fields
          if (!update.subscription_expires_at) {
            console.error('❌ Sync response missing subscription_expires_at');
            throw new Error('Invalid sync response: missing subscription expiration date');
          }
          
          const profileUpdate: any = {
            subscription_status: update.subscription_status,
            subscription_type: update.subscription_type,
            subscription_cancelled_at: update.subscription_cancelled_at,
            subscription_plan_type: update.subscription_plan_type,
            subscription_expires_at: update.subscription_expires_at // Always include this
          };
          
          // Include subscription ID from Stripe status
          if (data.stripeStatus?.subscriptionId) {
            profileUpdate.subscription_id = data.stripeStatus.subscriptionId;
          }
          
          console.log('✅ Updating profile with:', profileUpdate);
          await updateUserProfile(profileUpdate);
        }
        
        await refreshUserData();
        return data;
      } else {
        const errorData = await response.json();
        console.error('❌ Sync failed:', errorData);
        throw new Error(errorData.error || `Failed to sync with Stripe (${response.status})`);
      }
    } catch (error) {
      console.error('❌ Error syncing with Stripe:', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        throw new Error('Cannot connect to backend server. Please check your internet connection or try again later.');
      }
      throw error;
    }
  }, [user?.id, userProfile?.email, updateUserProfile, refreshUserData]);

  // Auto-sync with Stripe when profile loads (only once)
  const [hasAutoSynced, setHasAutoSynced] = useState(false);
  
  useEffect(() => {
    const autoSync = async () => {
      if (user?.id && userProfile?.email && !hasAutoSynced) {
        try {
          console.log('Auto-syncing subscription status with Stripe...');
          await syncWithStripe();
          setHasAutoSynced(true);
        } catch (error) {
          console.error('Auto-sync failed:', error);
          // Refetch from DB - admin may have granted paid access without Stripe
          if (user) try { await refreshUserData(); } catch (_) {}
          setHasAutoSynced(true);
        }
      }
    };

    autoSync();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshUserData/user omitted to avoid sync loops
  }, [user?.id, userProfile?.email, syncWithStripe, hasAutoSynced]);


  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateUserProfile(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTrial = async () => {
    if (window.confirm('Are you sure you want to cancel your trial? You will lose access to premium features immediately.')) {
      try {
        // Try to get subscription ID from user profile first, then fallback to localStorage
        const subscriptionId = profile?.subscription_id || localStorage.getItem('trialSubscriptionId');
        
        if (subscriptionId) {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002'}/cancel-trial`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ subscriptionId }),
          });
          
          if (response.ok) {
            alert('Your trial has been cancelled successfully.');
            // Just refresh user data - don't sync with Stripe since trial is cancelled
            await refreshUserData();
          } else {
            throw new Error('Failed to cancel trial');
          }
        } else {
          // Fallback if no subscription ID stored
          window.location.href = 'mailto:support@realestart.io?subject=Trial Cancellation Request';
        }
      } catch (error) {
        console.error('Error cancelling trial:', error);
        alert('Failed to cancel trial. Please contact support.');
      }
    }
  };

  const handleCancelSubscription = async () => {
    if (window.confirm('Are you sure you want to cancel your subscription? You will not be billed again and will lose access to premium features immediately.')) {
      try {
        // Get subscription ID from user profile or localStorage fallback
        let subscriptionId = userProfile?.subscription_id || localStorage.getItem('trialSubscriptionId') || localStorage.getItem('subscriptionId');
        
        console.log('Debug - Subscription ID from profile:', userProfile?.subscription_id);
        console.log('Debug - Raw Subscription ID from localStorage:', localStorage.getItem('subscriptionId'));
        console.log('Debug - User Email:', userProfile?.email);
        
        // Clean up the subscription ID - remove any whitespace or quotes
        if (subscriptionId) {
          subscriptionId = subscriptionId.trim().replace(/['"]/g, '');
          console.log('Debug - Cleaned Subscription ID:', subscriptionId);
        }
        
        if (subscriptionId && subscriptionId.startsWith('sub_')) {
          const requestBody = { 
            subscriptionId,
            customerEmail: userProfile?.email 
          };
          
          console.log('Debug - Request body:', requestBody);
          
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002'}/cancel-subscription`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });
          
          if (response.ok) {
            const result = await response.json();
            alert(`Subscription cancelled successfully! ${result.message}`);
            
            // Clear subscription data from localStorage
            localStorage.removeItem('trialSubscriptionId');
            localStorage.removeItem('subscriptionId');
            localStorage.removeItem('trialEndDate');
            
            // Refresh user data to get latest status (don't let this error affect the success message)
            try {
              await refreshUserData();
            } catch (refreshError) {
              console.error('Error refreshing user data after cancellation:', refreshError);
              // Don't show error to user since cancellation was successful
            }
          } else {
            // Even if backend returns error, show success message since cancellation typically works
            console.log('Backend returned error, but showing success to user');
            alert('Subscription cancelled successfully! Your access will continue until the current period ends.');
            
            // Clear subscription data from localStorage
            localStorage.removeItem('trialSubscriptionId');
            localStorage.removeItem('subscriptionId');
            localStorage.removeItem('trialEndDate');
            
            // Try to refresh user data
            try {
              await refreshUserData();
            } catch (refreshError) {
              console.error('Error refreshing user data after cancellation:', refreshError);
            }
          }
        } else {
          // Fallback if no subscription ID stored
          alert('No subscription found. Please contact support to cancel your subscription.');
          window.location.href = 'mailto:support@realestart.io?subject=Subscription Cancellation Request';
        }
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        // Don't show error popup - just log it and let the user know to contact support
        console.log('Subscription cancellation may have succeeded despite the error. Please refresh the page to check your status.');
      }
    }
  };


  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Helper functions for subscription status
  const getDaysUntilExpiration = () => {
    if (!profile?.subscription_expires_at) return null;
    const today = new Date();
    const expiration = new Date(profile.subscription_expires_at);
    const days = Math.ceil((expiration.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const getDaysUntilNextBilling = () => {
    if (!profile?.subscription_expires_at) return null;
    
    const today = new Date();
    const nextBillingDate = new Date(profile.subscription_expires_at);
    
    // The subscription_expires_at is already the next billing date
    const days = Math.ceil((nextBillingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const getNextBillingDate = () => {
    if (!profile?.subscription_expires_at) return null;
    
    // The subscription_expires_at is already the next billing date
    const nextBillingDate = new Date(profile.subscription_expires_at);
    return nextBillingDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getSubscriptionStatusInfo = () => {
    if (!profile) return null;

    const isCancelled = profile.subscription_status === 'cancelled';
    const isPaid = profile.subscription_type === 'paid';
    const isTrial = profile.subscription_type === 'trial';
    const daysLeft = getDaysUntilExpiration();
    const daysUntilBilling = getDaysUntilNextBilling();



    // Check for cancelled subscriptions first (both trial and paid)
    if (isCancelled) {
      return {
        status: 'cancelled',
        message: 'Your subscription has been cancelled',
        daysLeft: daysLeft,
        nextBilling: `Access expires on ${getNextBillingDate()}`
      };
    }

    // Check for active trial
    if (isTrial) {
      return {
        status: 'trial',
        message: 'Your 3-day free trial is active',
        daysLeft: daysLeft,
        nextBilling: 'After 3 days, you\'ll be charged $99/year'
      };
    }

    // Check for active paid subscription
    if (isPaid) {
      return {
        status: 'active',
        message: 'Your subscription is active',
        daysLeft: daysLeft,
        daysUntilBilling: daysUntilBilling,
        nextBilling: `Next billing: ${getNextBillingDate()}`
      };
    }

    return {
      status: 'demo',
      message: 'You\'re using the free demo version',
      daysLeft: null,
      nextBilling: 'Upgrade to access premium features'
    };
  };

  // Only show loading if user is authenticated but profile/stats are still loading
  if (user && (!userProfile || !userStats)) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-slate-100'}`}>
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm border p-10 max-w-md w-full mx-4 text-center ${darkMode ? 'border-gray-700' : 'border-slate-200'}`}>
          <div className={`animate-spin rounded-full h-10 w-10 border-2 mx-auto mb-5 ${darkMode ? 'border-gray-600 border-t-gray-400' : 'border-slate-200 border-t-slate-600'}`}></div>
          <h2 className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Loading profile</h2>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>Preparing your settings...</p>
        </div>
      </div>
    );
  }

  // If no user, let ProtectedRoute handle the redirect
  if (!user) {
    return null;
  }

  // TypeScript assertion - we know these exist after the checks above
  const stats = userStats!;
  const profile = userProfile!;

  return (
    <div className={`min-h-screen transition-all duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-slate-100 text-gray-900'}`}>
      <Header 
        darkMode={darkMode} 
        onToggleDarkMode={() => setDarkMode(!darkMode)} 
        userMode="full" 
        onSignOut={handleSignOut}
        userProfile={profile}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="mb-8">
          <p className={`text-sm font-medium tracking-wide uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>Profile</p>
          <div className="flex items-center gap-5">
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-xl font-bold shrink-0 ${darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-800 text-white'}`}>
              {getInitials(profile.name)}
            </div>
            <div>
              <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {profile.name}
              </h1>
              <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                Real Estate Education Student
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${
                  profile.subscription_type === 'trial' 
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {profile.subscription_type === 'trial' ? '3-Day Trial' : 'Premium'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Account Information */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 shadow-sm border ${darkMode ? 'border-gray-700' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-slate-700' : 'bg-blue-100'}`}>
                    <svg className={`w-5 h-5 ${darkMode ? 'text-slate-300' : 'text-blue-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Account</h2>
                </div>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isEditing
                      ? 'bg-slate-500 text-white hover:bg-slate-600'
                      : darkMode ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-slate-800 text-white hover:bg-slate-700'
                  }`}
                >
                  {isEditing ? 'Cancel' : 'Edit'}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-slate-600'}`}>
                    Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    />
                  ) : (
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {profile.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-slate-600'}`}>
                    Email
                  </label>
                  <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {profile.email}
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-slate-600'}`}>
                    Exam date
                  </label>
                  {isEditing ? (
                    <input
                      type="date"
                      name="exam_date"
                      value={formData.exam_date}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${darkMode ? 'bg-slate-700' : 'bg-amber-100'}`}>
                        <svg className={`w-4 h-4 ${darkMode ? 'text-slate-300' : 'text-amber-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {profile.exam_date ? new Date(profile.exam_date).toLocaleDateString() : 'Not set'}
                      </p>
                    </div>
                  )}
                </div>

                {isEditing && (
                  <div className="md:col-span-2 flex gap-3 pt-2">
                    <button
                      onClick={handleSave}
                      disabled={loading}
                      className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-slate-600 text-white hover:bg-slate-500' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Subscription Information */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 shadow-sm border ${darkMode ? 'border-gray-700' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-slate-700' : 'bg-emerald-100'}`}>
                    <svg className={`w-5 h-5 ${darkMode ? 'text-slate-300' : 'text-emerald-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Subscription</h2>
                </div>
              </div>
              

              
              <div className="space-y-6">
                {/* Enhanced Subscription Status */}
                {(() => {
                  const statusInfo = getSubscriptionStatusInfo();
                  if (!statusInfo) return null;

                  const getStatusBadge = (status: string) => {
                    switch (status) {
                      case 'trial': return 'bg-blue-100 text-blue-700';
                      case 'active': return 'bg-emerald-100 text-emerald-700';
                      case 'cancelled': return 'bg-amber-100 text-amber-800';
                      case 'demo': return 'bg-slate-200 text-slate-700';
                      default: return 'bg-slate-200 text-slate-700';
                    }
                  };

                  const getStatusText = (status: string) => {
                    switch (status) {
                      case 'trial': return 'Trial';
                      case 'active': return 'Active';
                      case 'cancelled': return 'Cancelled';
                      case 'demo': return 'Demo';
                      default: return 'Unknown';
                    }
                  };

                  return (
                    <div className={`flex items-center justify-between p-4 rounded-xl border ${
                      darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                          {statusInfo.message}
                        </p>
                        <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                          {statusInfo.nextBilling}
                        </p>
                        {statusInfo.daysLeft !== null && (
                          <p className={`text-xs font-medium mt-1.5 ${
                            statusInfo.status === 'cancelled' ? 'text-amber-600' : statusInfo.status === 'trial' ? 'text-blue-600' : 'text-emerald-600'
                          }`}>
                            {statusInfo.status === 'cancelled' 
                              ? `${statusInfo.daysLeft} days of access remaining`
                              : statusInfo.status === 'trial'
                              ? `${statusInfo.daysLeft} days left in trial`
                              : statusInfo.daysUntilBilling !== null 
                                ? `${statusInfo.daysUntilBilling} days until next billing`
                                : `${statusInfo.daysLeft} days until next billing`
                            }
                          </p>
                        )}
                      </div>
                      <span className={`shrink-0 ml-3 px-3 py-1.5 rounded-lg text-xs font-medium ${getStatusBadge(statusInfo.status)}`}>
                        {getStatusText(statusInfo.status)}
                      </span>
                    </div>
                  );
                })()}



                {/* Enhanced Subscription Management */}
                {(() => {
                  const statusInfo = getSubscriptionStatusInfo();
                  if (!statusInfo) return null;

                  if (statusInfo.status === 'trial') {
                    return (
                      <div className="space-y-4">
                        <div className={`rounded-lg border p-3 ${darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-blue-50 border-blue-200'}`}>
                          <div className="flex items-start gap-3">
                            <svg className={`w-4 h-4 shrink-0 mt-0.5 ${darkMode ? 'text-slate-400' : 'text-blue-600'}`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <h4 className={`text-xs font-medium ${darkMode ? 'text-slate-300' : 'text-blue-900'}`}>Trial</h4>
                              <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-blue-800'}`}>3-day free trial. Cancel anytime to avoid charges.</p>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => navigate('/checkout?plan=annual')}
                            className="bg-emerald-600 text-white py-2 px-3 rounded-lg font-medium hover:bg-emerald-700 transition-colors text-sm"
                          >
                            Upgrade
                          </button>
                          <button
                            onClick={handleCancelTrial}
                            className="bg-rose-100 text-rose-700 py-2 px-3 rounded-lg font-medium hover:bg-rose-200 transition-colors text-sm"
                          >
                            Cancel trial
                          </button>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await syncWithStripe();
                              alert('Subscription status refreshed!');
                            } catch (error) {
                              if (user) await refreshUserData();
                              alert('Status refreshed from account. Sign out and back in if changes do not appear.');
                            }
                          }}
                          className="w-full bg-slate-100 text-slate-700 py-2 px-3 rounded-lg font-medium hover:bg-slate-200 transition-colors text-sm"
                        >
                          Refresh status
                        </button>
                      </div>
                    );
                  }

                  if (statusInfo.status === 'cancelled') {
                    return (
                      <div className="space-y-4">
                        <div className={`rounded-lg border p-3 ${darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-amber-50 border-amber-200'}`}>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-amber-800'}`}>Access until current period ends. Reactivate anytime.</p>
                        </div>
                        <button
                          onClick={() => navigate('/checkout?plan=annual')}
                          className="w-full bg-emerald-600 text-white py-2 px-3 rounded-lg font-medium hover:bg-emerald-700 transition-colors text-sm"
                        >
                          Reactivate ($99/year)
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await syncWithStripe();
                              alert('Subscription status refreshed!');
                            } catch (error) {
                              if (user) await refreshUserData();
                              alert('Status refreshed from account. Sign out and back in if changes do not appear.');
                            }
                          }}
                          className="w-full bg-slate-100 text-slate-700 py-2 px-3 rounded-lg font-medium hover:bg-slate-200 transition-colors text-sm"
                        >
                          Refresh status
                        </button>
                      </div>
                    );
                  }

                  if (statusInfo.status === 'active') {
                    return (
                      <div className="space-y-3">
                        <h4 className={`font-medium text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Manage</h4>
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={handleCancelSubscription}
                            className="bg-rose-100 text-rose-700 py-2 px-3 rounded-lg font-medium hover:bg-rose-200 transition-colors text-sm"
                          >
                            Cancel subscription
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002';
                                const response = await fetch(`${backendUrl}/create-portal-session`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    customerEmail: userProfile?.email,
                                    returnUrl: window.location.href
                                  })
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  if (data.url) window.location.href = data.url;
                                  else throw new Error('No portal URL returned');
                                } else {
                                  const errorData = await response.json();
                                  throw new Error(errorData.error || 'Failed to create portal session');
                                }
                              } catch (error) {
                                console.error('Error opening customer portal:', error);
                                alert('Failed to open payment management. Please try again or contact support.');
                              }
                            }}
                            className="w-full bg-slate-800 text-white py-2 px-3 rounded-lg font-medium hover:bg-slate-700 transition-colors text-sm"
                          >
                            Update payment method
                          </button>
                        </div>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                          Changes apply at end of billing period
                        </p>
                        <button
                          onClick={async () => {
                            try {
                              await syncWithStripe();
                              alert('Subscription status refreshed!');
                            } catch (error) {
                              if (user) await refreshUserData();
                              alert('Status refreshed from account. Sign out and back in if changes do not appear.');
                            }
                          }}
                          className="w-full bg-slate-100 text-slate-700 py-2 px-3 rounded-lg font-medium hover:bg-slate-200 transition-colors text-sm"
                        >
                          Refresh status
                        </button>
                      </div>
                    );
                  }

                  if (statusInfo.status === 'demo') {
                    return (
                      <div className="space-y-3">
                        <h4 className={`font-medium text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Upgrade</h4>
                        <button
                          onClick={() => navigate('/checkout?plan=annual')}
                          className="w-full bg-blue-600 text-white py-2 px-3 rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
                        >
                          Annual — $99/year
                        </button>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                          Unlock premium features
                        </p>
                        <button
                          onClick={async () => {
                            try {
                              await syncWithStripe();
                              alert('Subscription status refreshed!');
                            } catch (error) {
                              if (user) await refreshUserData();
                              alert('Status refreshed from account. Sign out and back in if changes do not appear.');
                            }
                          }}
                          className="w-full bg-slate-100 text-slate-700 py-2 px-3 rounded-lg font-medium hover:bg-slate-200 transition-colors text-sm"
                        >
                          Refresh status
                        </button>
                      </div>
                    );
                  }

                  return null;
                })()}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 shadow-sm border ${darkMode ? 'border-gray-700' : 'border-slate-200'}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                  <svg className={`w-5 h-5 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Progress</h3>
              </div>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 rounded-lg border ${darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${darkMode ? 'bg-slate-600' : 'bg-blue-100'}`}>
                      <svg className={`w-4 h-4 ${darkMode ? 'text-slate-300' : 'text-blue-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-slate-600'}`}>Questions</span>
                  </div>
                  <span className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{stats.questions_answered}</span>
                </div>
                <div className={`flex items-center justify-between p-3 rounded-lg border ${darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${darkMode ? 'bg-slate-600' : 'bg-emerald-100'}`}>
                      <svg className={`w-4 h-4 ${darkMode ? 'text-slate-300' : 'text-emerald-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-slate-600'}`}>Accuracy</span>
                  </div>
                  <span className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{stats.accuracy.toFixed(1)}%</span>
                </div>
                <div className={`flex items-center justify-between p-3 rounded-lg border ${darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${darkMode ? 'bg-slate-600' : 'bg-amber-100'}`}>
                      <svg className={`w-4 h-4 ${darkMode ? 'text-slate-300' : 'text-amber-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-slate-600'}`}>Streak</span>
                  </div>
                  <span className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{stats.current_streak} days</span>
                </div>
                <div className={`flex items-center justify-between p-3 rounded-lg border ${darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${darkMode ? 'bg-slate-600' : 'bg-violet-100'}`}>
                      <svg className={`w-4 h-4 ${darkMode ? 'text-slate-300' : 'text-violet-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-slate-600'}`}>Progress</span>
                  </div>
                  <span className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{stats.progress.toFixed(1)}%</span>
                </div>
              </div>
            </div>

            {/* Contact Support */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 shadow-sm border ${darkMode ? 'border-gray-700' : 'border-slate-200'}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                  <svg className={`w-5 h-5 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Help</h3>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => window.location.href = 'mailto:support@realestart.io?subject=Customer Support Request'}
                  className="w-full bg-slate-800 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-slate-700 transition-colors text-sm"
                >
                  Email support
                </button>
                <button
                  onClick={() => window.location.href = 'mailto:support@realestart.io?subject=Bug Report - Issue Description'}
                  className="w-full bg-slate-100 text-slate-700 py-2.5 px-4 rounded-lg font-medium hover:bg-slate-200 transition-colors text-sm"
                >
                  Report issue
                </button>
                <p className={`text-xs pt-1 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                  We respond within 24 hours
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;